/**
 * Channel management and per-user sidebar organization DTOs (REST /v1). Events: `channel.updated`,
 * `channel.member_left` (realtime.ts) for channel changes; `membership.updated`, `sidebar_item.updated`,
 * `sidebar_folder.updated` and `sidebar_folder.deleted` go only to the member themself (their other devices).
 */
import { z } from "zod";
import { Channel, ChannelMembership, Id, NotifyLevel, SidebarFolder, SidebarItem, SidebarItemKind, Timestamp } from "./entities.ts";

/** Fractional-indexing key (base-62 digits; compared as plain strings). */
export const SidebarRank = z.string().min(1).max(128).regex(/^[0-9A-Za-z]+$/);

/** Browse channels: every public channel (archived ones with `archived=true`) plus private channels I belong to. */
export const ListChannelsQuery = z.object({ archived: z.coerce.boolean().optional() });
export const ListChannelsResponse = z.object({ channels: z.array(Channel) });
export type ListChannelsResponse = z.infer<typeof ListChannelsResponse>;

/**
 * Channel settings. Rename, convert (`kind`) and archive/unarchive: workspace admins/owners or the channel's creator.
 * Topic and purpose: any member. An empty topic/purpose clears it. Archived channels are read-only (no posts,
 * reactions or edits) and leave everyone's sidebar, but stay searchable and browsable. DMs only take a topic.
 * A group DM converts to a private channel ("Convert to private channel", Slack) with `kind: "private"` and a `name`,
 * by any of its members who may create channels; members and history stay.
 */
export const UpdateChannelRequest = z.object({
  name: z.string().min(1).max(80).optional(),
  topic: z.string().max(250).optional(),
  purpose: z.string().max(250).optional(),
  kind: z.enum(["public", "private"]).optional(),
  archived: z.boolean().optional(),
});
export type UpdateChannelRequest = z.infer<typeof UpdateChannelRequest>;

/** My settings for one conversation. Starring takes it out of its folder (to my Starred section). */
export const UpdateMembershipRequest = z.object({
  starred: z.boolean().optional(),
  muted: z.boolean().optional(),
  notify: NotifyLevel.optional(),
  /** DMs / group DMs: close (hide from my sidebar until the next message) or reopen. */
  closed: z.boolean().optional(),
});
export type UpdateMembershipRequest = z.infer<typeof UpdateMembershipRequest>;
export const MembershipResponse = z.object({ membership: ChannelMembership });
export type MembershipResponse = z.infer<typeof MembershipResponse>;

/**
 * One drag-and-drop result: where an item now lives in my sidebar. Conversations (`channel` / `dm`): `starred` → my
 * Starred section; else `folderId` → that folder; else their default section. Docs items (`page`): `folderId` → that
 * folder; else back in the page tree (`starred` doesn't apply). Absent `rank` = default order.
 */
export const SidebarMove = z.object({
  kind: SidebarItemKind,
  id: Id,
  starred: z.boolean().optional(),
  folderId: Id.optional(),
  rank: SidebarRank.optional(),
});
export type SidebarMove = z.infer<typeof SidebarMove>;

/** A drop can rank several items at once (the first manual order of a section ranks the rows above the drop too). */
export const MoveSidebarItemsRequest = z.object({ moves: z.array(SidebarMove).min(1).max(500) });
export type MoveSidebarItemsRequest = z.infer<typeof MoveSidebarItemsRequest>;
/** The moved items' placements, and the memberships whose star changed. */
export const MoveSidebarItemsResponse = z.object({ items: z.array(SidebarItem), memberships: z.array(ChannelMembership) });
export type MoveSidebarItemsResponse = z.infer<typeof MoveSidebarItemsResponse>;

export const SIDEBAR_FOLDER_NAME_MAX = 80;
export const SIDEBAR_FOLDERS_MAX = 50;

/** `id`: a client-generated ULID (offline-first); replays with the same id return the folder. */
export const CreateSidebarFolderRequest = z.object({
  id: Id.optional(),
  name: z.string().min(1).max(SIDEBAR_FOLDER_NAME_MAX),
  emoji: z.string().max(32).optional(),
  icon: z.string().max(40).optional(),
  /** Absent → after my last folder. */
  rank: SidebarRank.optional(),
});
export type CreateSidebarFolderRequest = z.infer<typeof CreateSidebarFolderRequest>;

/** An empty emoji / icon clears it. */
export const UpdateSidebarFolderRequest = z.object({
  name: z.string().min(1).max(SIDEBAR_FOLDER_NAME_MAX).optional(),
  emoji: z.string().max(32).optional(),
  icon: z.string().max(40).optional(),
  rank: SidebarRank.optional(),
  collapsed: z.boolean().optional(),
});
export type UpdateSidebarFolderRequest = z.infer<typeof UpdateSidebarFolderRequest>;
export const SidebarFolderResponse = z.object({ folder: SidebarFolder });
export type SidebarFolderResponse = z.infer<typeof SidebarFolderResponse>;

// ---------- Channel members ----------

/** Someone in a channel (profiles come from the workspace's members). */
export const ChannelMember = z.object({ userId: Id, joinedAt: Timestamp });
export type ChannelMember = z.infer<typeof ChannelMember>;

/**
 * Who is in a channel (anyone who can read it). `isDefault`: the workspace's #general — everyone is in it, nobody leaves
 * or is removed.
 */
export const ListChannelMembersResponse = z.object({ members: z.array(ChannelMember), isDefault: z.boolean() });
export type ListChannelMembersResponse = z.infer<typeof ListChannelMembersResponse>;

/**
 * Add people to a channel I'm in (not DMs, not archived). Restricted members (guests, agents) need `can.invite`; people
 * must be in my directory (`can.seeDirectory`), and restricted people are granted the channel within their
 * `maxChannels`. Already-members are skipped. Emits `channel.member_joined` + `channel.updated`.
 */
export const AddChannelMembersRequest = z.object({ userIds: z.array(Id).min(1).max(100) });
export type AddChannelMembersRequest = z.infer<typeof AddChannelMembersRequest>;
